import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";

const SITE_ORIGIN = "https://www.ghoststudio.net";
const SUPPORTED_LANGUAGES = ["ko", "en", "cn"];

const SEO_CONTENT = {
  home: {
    ko: {
      title: "GHOST STUDIO | GhostStudio 공식 사이트",
      description: "GhostStudio(고스트스튜디오) 공식 사이트입니다. 아티스트, 드라마, 웹툰, 게임, 뉴스 정보를 확인할 수 있습니다.",
    },
    en: {
      title: "GHOST STUDIO | GhostStudio Official Website",
      description: "Official GhostStudio website with artists, dramas, webtoons, games, and company news.",
    },
    cn: {
      title: "GHOST STUDIO | GhostStudio 官方网站",
      description: "GhostStudio 官方网站，提供艺人、电视剧、漫画、游戏与新闻资讯。",
    },
  },
  dramas: {
    ko: {
      title: "Dramas | GHOST STUDIO",
      description: "GhostStudio 드라마 콘텐츠와 관련 소식을 확인하세요.",
    },
    en: {
      title: "Dramas | GHOST STUDIO",
      description: "Explore drama content and updates from GhostStudio.",
    },
    cn: {
      title: "Dramas | GHOST STUDIO",
      description: "查看 GhostStudio 的电视剧内容与最新动态。",
    },
  },
  news: {
    ko: {
      title: "News | GHOST STUDIO",
      description: "GhostStudio의 최신 뉴스와 공지사항을 확인하세요.",
    },
    en: {
      title: "News | GHOST STUDIO",
      description: "Read the latest GhostStudio news and announcements.",
    },
    cn: {
      title: "News | GHOST STUDIO",
      description: "了解 GhostStudio 的最新新闻与公告。",
    },
  },
  ghoststudio: {
    ko: {
      title: "Ghost Studio | 회사 소개",
      description: "GhostStudio 회사 소개와 핵심 사업 정보를 확인하세요.",
    },
    en: {
      title: "Ghost Studio | About",
      description: "Learn more about GhostStudio and its core business areas.",
    },
    cn: {
      title: "Ghost Studio | 公司介绍",
      description: "了解 GhostStudio 的公司介绍与核心业务。",
    },
  },
};

function normalizePath(pathname, language) {
  const prefix = `/${language}`;
  if (!pathname.startsWith(prefix)) {
    return "/";
  }

  const stripped = pathname.slice(prefix.length);
  return stripped || "/";
}

function getSeoKey(pathname) {
  if (pathname === "/") {
    return "home";
  }
  if (pathname === "/dramas") {
    return "dramas";
  }
  if (pathname.startsWith("/news")) {
    return "news";
  }
  if (pathname.startsWith("/ghoststudio")) {
    return "ghoststudio";
  }
  return "home";
}

export default function SeoHead() {
  const location = useLocation();
  const { language } = useParams();
  const currentLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : "en";

  const normalizedPath = normalizePath(location.pathname, currentLanguage);
  const seoKey = getSeoKey(normalizedPath);
  const seo = SEO_CONTENT[seoKey][currentLanguage];
  const canonical = `${SITE_ORIGIN}${location.pathname}`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={currentLanguage === "cn" ? "zh-CN" : currentLanguage} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="GHOST STUDIO | GhostStudio" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={`${SITE_ORIGIN}/images/Cover_sns.png`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={`${SITE_ORIGIN}/images/Cover_sns.png`} />

      <link
        rel="alternate"
        hrefLang="ko-KR"
        href={`${SITE_ORIGIN}/ko${normalizedPath === "/" ? "" : normalizedPath}`}
      />
      <link
        rel="alternate"
        hrefLang="en"
        href={`${SITE_ORIGIN}/en${normalizedPath === "/" ? "" : normalizedPath}`}
      />
      <link
        rel="alternate"
        hrefLang="zh-CN"
        href={`${SITE_ORIGIN}/cn${normalizedPath === "/" ? "" : normalizedPath}`}
      />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}/en`} />
    </Helmet>
  );
}
